import { db } from '../firebaseConfig';
import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { Reclamacion } from '../models/types';

// Colección de Firestore
const COLLECTION_NAME = 'reclamaciones';

// Servicio para gestionar reclamaciones en Firestore
export const reclamacionServiceFirestore = {
  // Obtener todas las reclamaciones
  getAll: async (): Promise<Reclamacion[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Reclamacion));
    } catch (error) {
      console.error('Error al obtener reclamaciones:', error);
      return [];
    }
  },

  // Obtener reclamaciones por estado
  getByEstado: async (estado: 'pendiente' | 'aceptada' | 'rechazada' | 'sancionada'): Promise<Reclamacion[]> => {
    try {
      const q = query(collection(db, COLLECTION_NAME), where('estado', '==', estado));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Reclamacion));
    } catch (error) {
      console.error(`Error al obtener reclamaciones con estado ${estado}:`, error);
      return [];
    }
  },

  // Obtener reclamaciones por piloto reclamante
  getByReclamante: async (reclamanteSocioId: string): Promise<Reclamacion[]> => {
    try {
      const q = query(collection(db, COLLECTION_NAME), where('reclamanteSocioId', '==', reclamanteSocioId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Reclamacion));
    } catch (error) {
      console.error(`Error al obtener reclamaciones del piloto ${reclamanteSocioId}:`, error);
      return [];
    }
  },

  // Obtener reclamaciones por piloto reclamado
  getByReclamado: async (reclamadoSocioId: string): Promise<Reclamacion[]> => {
    try {
      const q = query(collection(db, COLLECTION_NAME), where('reclamadoSocioId', '==', reclamadoSocioId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Reclamacion));
    } catch (error) {
      console.error(`Error al obtener reclamaciones contra el piloto ${reclamadoSocioId}:`, error);
      return [];
    }
  },

  // Obtener reclamaciones por carrera
  getByCarrera: async (carreraId: string): Promise<Reclamacion[]> => {
    try {
      const q = query(collection(db, COLLECTION_NAME), where('carreraId', '==', carreraId));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Reclamacion));
    } catch (error) {
      console.error(`Error al obtener reclamaciones de la carrera ${carreraId}:`, error);
      return [];
    }
  },

  // Obtener una reclamación por ID
  getById: async (id: string): Promise<Reclamacion | null> => {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Reclamacion;
      } else {
        console.log(`No se encontró la reclamación con ID ${id}`);
        return null;
      }
    } catch (error) {
      console.error(`Error al obtener la reclamación ${id}:`, error);
      return null;
    }
  },

  // Crear una nueva reclamación
  create: async (reclamacion: Omit<Reclamacion, 'id'>): Promise<Reclamacion | null> => {
    try {
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...reclamacion,
        fechaReclamacion: reclamacion.fechaReclamacion || new Date().toISOString().split('T')[0],
        estado: reclamacion.estado || 'pendiente'
      });
      
      return {
        id: docRef.id,
        ...reclamacion
      } as Reclamacion;
    } catch (error) {
      console.error('Error al crear la reclamación:', error);
      return null;
    }
  },

  // Actualizar una reclamación existente
  update: async (id: string, reclamacion: Partial<Reclamacion>): Promise<boolean> => {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(docRef, reclamacion);
      return true;
    } catch (error) {
      console.error(`Error al actualizar la reclamación ${id}:`, error);
      return false;
    }
  },

  // Eliminar una reclamación
  delete: async (id: string): Promise<boolean> => {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error(`Error al eliminar la reclamación ${id}:`, error);
      return false;
    }
  },

  // Cambiar el estado de una reclamación
  cambiarEstado: async (id: string, nuevoEstado: 'pendiente' | 'aceptada' | 'rechazada' | 'sancionada', comentarios?: string): Promise<boolean> => {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const updateData: Partial<Reclamacion> = {
        estado: nuevoEstado,
        fechaResolucion: new Date().toISOString().split('T')[0]
      };
      
      if (comentarios) {
        updateData.comentariosComisarios = comentarios;
      }
      
      await updateDoc(docRef, updateData);
      return true;
    } catch (error) {
      console.error(`Error al cambiar el estado de la reclamación ${id}:`, error);
      return false;
    }
  },

  // Asociar una sanción a una reclamación
  asociarSancion: async (reclamacionId: string, sancionId: string): Promise<boolean> => {
    try {
      const docRef = doc(db, COLLECTION_NAME, reclamacionId);
      await updateDoc(docRef, {
        sancionId,
        estado: 'sancionada',
        fechaResolucion: new Date().toISOString().split('T')[0]
      });
      return true;
    } catch (error) {
      console.error(`Error al asociar la sanción ${sancionId} a la reclamación ${reclamacionId}:`, error);
      return false;
    }
  }
};
